import { apiClient } from './apiClient';

export interface UserProfile {
  id: string;
  displayName: string;
  givenName: string;
  surname: string;
  userPrincipalName: string;
  mail: string;
  jobTitle?: string;
  officeLocation?: string;
  businessPhones: string[];
  mobilePhone?: string;
}

/**
 * GraphService - Routes all Microsoft Graph calls through the backend via OBO flow
 *
 * This service no longer calls Graph directly. Instead, it uses the backend API
 * which performs the OBO (On-Behalf-Of) token exchange to access Graph securely.
 *
 * Benefits of routing through backend:
 * - Tokens never exposed in browser
 * - Server-side logging and auditing
 * - Consistent error handling
 * - Follows Microsoft's recommended pattern for multi-tier apps
 */
export class GraphService {
  /**
   * Fetch user profile information via backend OBO flow
   * @param _accessToken - Not used directly, backend extracts from Authorization header
   */
  static async getUserProfile(_accessToken: string): Promise<UserProfile | null> {
    try {
      const response = await apiClient.get<UserProfile>('/api/chat/user/profile');
      return response;
    } catch (error) {
      console.error('❌ Error fetching user profile:', error);
      return null;
    }
  }

  /**
   * Fetch user profile picture via backend OBO flow
   * Returns a data URI that can be used as image src (CSP compliant)
   * @param _accessToken - Not used directly, backend extracts from Authorization header
   */
  static async getUserPhoto(_accessToken: string): Promise<string | null> {
    try {
      const response = await apiClient.get<{ photoData: string }>('/api/chat/user/photo');
      return response?.photoData || null;
    } catch (error: any) {
      // 404 is expected when user has no photo
      if (error?.status === 404) {
        return null;
      }
      console.error('❌ Error fetching user photo:', error);
      return null;
    }
  }

  /**
   * Get user photo metadata (dimensions, content type)
   * Note: This is now routed through backend - returns null as metadata
   * endpoint is not implemented (photo data is sufficient)
   * @param _accessToken - Not used
   */
  static async getUserPhotoMetadata(_accessToken: string): Promise<any | null> {
    // Photo metadata is not needed when using the backend endpoint
    // The backend returns the full photo data with proper content type
    return null;
  }
}

export default GraphService;
